import { Business, TenantBusiness } from "@turizi/shared";
import { BUSINESS_TEMPLATES } from "@/types/templates";
import { TagIcon } from "@/lib/tag-utils";
import { IconDiscount, IconMail, IconStar, IconExternalLink, IconCoins, IconFileDescription } from "@tabler/icons-react";
import { getAttrValue } from "@/lib/models-utils";
import { Badge } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { SkeletonListWithFilters } from "./snippets";
import { useEffect, useMemo, useState } from "react";
import { localTenant } from "@/services/session-service";
import { Link } from "react-router-dom";


interface Props {
    business: Business;
    isLoading?: boolean;
    className?: string;
    size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
    showTariffsLink?: boolean;
}


export default function BusinessCardPrivate({
    business,
    isLoading = false,
    className,
    size = 'sm',
    showTariffsLink = true
}: Props) {
    const { t } = useTranslation();
    const tenant = localTenant();
    const [tenantBusiness, setTenantBusiness] = useState<TenantBusiness | null>(null);

    const emailCount = useMemo(() => {
        return business.emails ? Object.values(business.emails).length : 0;
    }, [business.emails]);

    useEffect(() => {
        if (business.tenant_business) {
            setTenantBusiness(business.tenant_business);
        }
    }, [business]);

    if (isLoading) {
        return (
            <div className={className}>
                <SkeletonListWithFilters />
            </div>
        );
    }

    if (!business) {
        return null;
    }

    if (business.category === BUSINESS_TEMPLATES.hotel.category) {
        const stars = getAttrValue(business, 'stars');
        const regime = getAttrValue(business, 'regime');
        const swimmingPool = getAttrValue(business, 'swimming_pool');

        return (
            <div className={`${className || 'solid-box-border'} flex items-start gap-2 justify-between`}>
                <div>
                    <TagIcon
                        name={business?.category}
                        size={size}
                        hideText
                    />
                    <div className="text-gray-400 text-xxs text-center">
                        <span className="text-gray-400 text-[0.5rem]">#</span>{business.id}
                    </div>
                </div>
                <div className="flex-1">
                    <div className="text-xxs font-medium text-gray-400">
                        {t(business.category)}
                    </div>
                    <div className="flex flex-wrap items-center gap-1">
                        <div className={`text-sm font-medium`}>
                            {business.name}
                        </div>
                        <div className="flex items-center gap-0.5">
                            {Array.from({ length: stars }).map((_, i) => (
                                <IconStar key={i} size={16} color="#f59e0b" fill="#f59e0b" stroke={1} />
                            ))}
                        </div>
                    </div>
                    <div className="text-xs font-medium text-gray-400">
                        {business.city}, {business.country_code}
                    </div>
                    <div className="flex flex-wrap gap-1">
                        {regime && (
                            <Badge color="green" variant="light" size="xs">
                                {regime}
                            </Badge>
                        )}
                        {swimmingPool && (
                            <Badge color="black" variant="light" size="xs">
                                {t('swimming_pool')}
                            </Badge>
                        )}
                        {tenantBusiness?.is_favorite && (
                            <Badge color="yellow" variant="light" size="xs">
                                {t('favorite')}
                            </Badge>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div
            className={
                `${className || 'solid-box-border'} 
                flex items-start gap-2 justify-between`
            }
        >
            <div>
                <TagIcon
                    name={business?.category}
                    size={size}
                    hideText
                />
                <div className="text-gray-400 text-xxs text-center">
                    <span className="text-gray-400 text-[0.5rem]">#</span>{business.id}
                </div>
            </div>
            <div className="flex-1">
                <div className="text-xxs font-medium text-gray-400">
                    {t(business.category)}
                </div>
                <div className={`text-sm font-medium`}>
                    {business.name}
                </div>
                <div className="text-xs font-medium text-gray-400">
                    {business.city} {business.country_code}
                </div>
                <div className="flex flex-wrap gap-1">
                    {tenantBusiness?.is_favorite && (
                        <Badge color="yellow" variant="light" size="xs">
                            {t('favorite')}
                        </Badge>
                    )}
                </div>
            </div>
            <div className="space-y-2 flex flex-col items-end">
                <div className="flex flex-wrap gap-1.5 justify-end">
                    {(business?.tax_id_type || business?.tax_id_number) && (
                        <Badge
                            color="teal"
                            variant="light"
                            size="xs"
                            leftSection={<IconFileDescription size={12} />}
                        >
                            {business.tax_id_type || 'Tax ID'}: {business.tax_id_number || '--'}
                        </Badge>
                    )}
                    {tenantBusiness?.default_currency && (
                        <Badge
                            color="orange"
                            variant="light"
                            size="xs"
                            leftSection={<IconCoins size={12} />}
                        >
                            {tenantBusiness.default_currency}
                        </Badge>
                    )}
                    <Badge
                        color="blue"
                        variant="light"
                        size="xs"
                        leftSection={<IconDiscount size={12} />}
                    >
                        {tenantBusiness?.discount_category || t('no_discount')}
                    </Badge>
                </div>
                <div className="flex gap-2.5 items-center text-xs">
                    {emailCount === 0 && (
                        <Badge
                            color="red"
                            variant="light"
                            size="xs"
                            leftSection={<IconMail size={12} />}
                        >
                            {emailCount}
                        </Badge>
                    )}
                </div>
            </div>
        </div>
    );
}